import { useEffect, useRef } from 'react';

export const AdminPollingIntervalMs = 60_000;

type RefreshDashboard = () => void | Promise<void>;

const isPageVisible = () => document.visibilityState === 'visible';

export const useAdminPolling = (
  refresh: RefreshDashboard,
  intervalMs = AdminPollingIntervalMs
) => {
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    let disposed = false;
    let inFlight = false;
    let pollTimer: number | null = null;

    const runRefresh = () => {
      if (disposed || inFlight) {
        return;
      }

      inFlight = true;
      void Promise.resolve()
        .then(() => refreshRef.current())
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    };

    const stopPolling = () => {
      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const startPolling = () => {
      if (!isPageVisible() || pollTimer !== null || intervalMs <= 0) {
        return;
      }

      pollTimer = window.setInterval(runRefresh, intervalMs);
    };

    const handleVisibilityChange = () => {
      if (!isPageVisible()) {
        stopPolling();
        return;
      }

      runRefresh();
      startPolling();
    };

    const handleForegroundSignal = () => {
      if (isPageVisible()) {
        runRefresh();
      }
    };

    if (isPageVisible()) {
      runRefresh();
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleForegroundSignal);
    window.addEventListener('online', handleForegroundSignal);

    return () => {
      disposed = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleForegroundSignal);
      window.removeEventListener('online', handleForegroundSignal);
    };
  }, [intervalMs]);
};

export default useAdminPolling;
